import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/auth-context';
import { useCart } from '../contexts/cart-context';
import { Card, CardBody, Button } from '@heroui/react';
import { Icon } from '@iconify/react';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PayStatusPage() {
  const query = useQuery();
  const history = useHistory();
  const orderId = query.get('order_id');
  const statusQuery = (query.get('status') || '').toLowerCase();

  const [loading, setLoading] = React.useState(true);
  const [serverChecked, setServerChecked] = React.useState(false);
  const [orderExists, setOrderExists] = React.useState<boolean | null>(null);
  const [orderInfo, setOrderInfo] = React.useState<any>(null);
  const [status, setStatus] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [cartCleared, setCartCleared] = React.useState(false);
  const { clearCart } = useCart();

  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    if (!orderId) {
      setError('Не указан идентификатор заказа (order_id) в URL');
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        // Check payment status endpoint as implemented on backend
        const resp = await api.get(`/orders/${encodeURIComponent(orderId)}/payment-status/`);
        if (!mounted) return;
        setOrderInfo(resp.data || null);
        setOrderExists(true);
        const serverPaymentStatus = resp.data?.payment_status || null;
        setStatus(serverPaymentStatus || statusQuery || null);
        setServerChecked(true);

        // If server says pending and bank redirected with status in query, report it to backend
        if (serverPaymentStatus === 'pending' && statusQuery) {
          const desired = statusQuery === 'success' ? 'success' : statusQuery === 'failed' ? 'failed' : null;
          if (desired) {
            try {
              const postResp = await api.post(`/orders/${encodeURIComponent(orderId)}/payment-status/`, { payment_status: desired }, { headers: { 'Content-Type': 'application/json' } });
              if (!mounted) return;
              const updated = postResp.data;
              setOrderInfo(updated || resp.data);
              setStatus(updated?.payment_status || desired);
            } catch (pe: any) {
              console.error('Failed to POST payment-status', pe);
              setError(pe?.response?.data?.message || 'Не удалось обновить статус на сервере');
            }
          }
        }
      } catch (e: any) {
        console.warn('Order lookup failed', e);
        setOrderExists(false);
        setStatus(statusQuery || null);
      } finally {
        setLoading(false);
      }
    })();

    return () => { mounted = false };
  }, [orderId, statusQuery]);

  const handleRetryPayment = async () => {
    if (!orderId) return;
    setActionLoading(true);
    try {
      const resp = await api.post('/payments/init/', { order_id: orderId }, { headers: { 'Content-Type': 'application/json' } });
      const data = resp.data;
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl;
        return;
      }
      setError('Не удалось получить ссылку на оплату. Свяжитесь с поддержкой.');
    } catch (e: any) {
      console.error('Retry payment failed', e);
      setError(e?.response?.data?.message || 'Ошибка инициации платежа');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmServer = async () => {
    // If bank redirected with status=success and server still thinks payment is pending,
    // call backend to update order status. Backend must implement this endpoint.
    if (!orderId) return;
    setActionLoading(true);
    try {
      const resp = await api.post(`/orders/${encodeURIComponent(orderId)}/confirm-payment/`, { status: status || 'success' }, { headers: { 'Content-Type': 'application/json' } });
      setOrderInfo(resp.data);
      setStatus(resp.data?.payment_status || status);
      setError(null);
    } catch (e: any) {
      console.error('Confirm payment failed', e);
      setError(e?.response?.data?.message || 'Ошибка подтверждения платежа на сервере');
    } finally {
      setActionLoading(false);
    }
  };

  const isSuccess = status === 'success' || status === 'paid' || statusQuery === 'success';

  // Clear cart when payment is confirmed successful/paid
  React.useEffect(() => {
    if (!cartCleared && isSuccess) {
      try {
        clearCart();
        setCartCleared(true);
      } catch (e) {
        console.warn('Failed to clear cart after payment success', e);
      }
    }
  }, [isSuccess, cartCleared, clearCart]);

  return (
    <section className="py-24 bg-background min-h-screen">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center">
            <CardBody>
              {loading ? (
                <div className="py-12">
                  <div className="animate-pulse h-48">
                    <div className="w-24 h-24 mx-auto rounded-full bg-content2 mb-6" />
                    <div className="h-6 bg-content2 rounded w-3/4 mx-auto mb-2" />
                    <div className="h-4 bg-content2 rounded w-1/2 mx-auto" />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-6">
                    <div className={`w-28 h-28 mx-auto rounded-full flex items-center justify-center ${isSuccess ? 'bg-success/10' : 'bg-danger/10'}`}>
                      <Icon icon={isSuccess ? 'lucide:check' : 'lucide:x'} className={`${isSuccess ? 'text-success' : 'text-danger'}`} width={56} />
                    </div>
                  </div>

                  <h2 className="text-2xl font-medium mb-2">{isSuccess ? 'Оплата прошла' : 'Оплата не прошла'}</h2>
                  <p className="text-foreground/70 mb-6">
                    {isSuccess ? 'Деньги успешно списаны. Спасибо — заказ передан в обработку.' : 'К сожалению, оплата не была завершена. Вы можете повторить попытку оплаты или связаться с поддержкой.'}
                  </p>

                  <div className="mb-4">
                    <p className="text-sm text-foreground/70">Номер заказа</p>
                    <p className="font-medium mt-1">{orderId}</p>
                  </div>

                  {orderInfo?.total && (
                    <div className="mb-4">
                      <p className="text-sm text-foreground/70">Сумма</p>
                      <p className="font-medium mt-1">{orderInfo.total} ₽</p>
                    </div>
                  )}

                  {error && <p className="text-danger text-sm mb-4">{error}</p>}

                  <div className="flex gap-3 justify-center flex-wrap">
                    {isSuccess ? (
                      <>
                        {isAuthenticated && (
                          <Button color="primary" onPress={() => history.push(`/account/orders/${orderId}`)}>Посмотреть заказ</Button>
                        )}
                        <Button variant="outline" onPress={() => history.push('/catalog')}>Продолжить покупки</Button>
                      </>
                    ) : (
                      <>
                        <Button color="primary" isLoading={actionLoading} onPress={handleRetryPayment}>Повторить оплату</Button>
                        <Button variant="outline" onPress={() => window.location.href = 'mailto:orders@example.com'}>Связаться с поддержкой</Button>
                        <Button variant="ghost" onPress={() => history.push('/cart')}>Вернуться в корзину</Button>
                      </>
                    )}
                  </div>

                  {/* If bank said success but server still pending, offer explicit confirm button */}
                  {!loading && serverChecked && isSuccess && orderExists && orderInfo?.payment_status === 'payment-pending' && (
                    <div className="mt-6">
                      <p className="text-sm text-foreground/70 mb-2">Статус платежа в системе — ожидается подтверждение.</p>
                      <div className="flex justify-center">
                        <Button color="primary" isLoading={actionLoading} onPress={handleConfirmServer}>Подтвердить платёж на сервере</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </section>
  );
}
